// Intervals.icu API integration — supports both API key (Basic Auth) and OAuth2 (Bearer)
import { normalizeActivityType } from './utils/activityTypes';

export class IntervalsAPI {
  constructor(credential, athleteId, useOAuth = false) {
    this.athleteId = athleteId;
    this.useOAuth = useOAuth;
    this.baseURL = 'https://intervals.icu/api/v1';
    this.authURL = 'https://intervals.icu/oauth/authorize';
    this.tokenProxyURL = '/api/intervalsToken';
    this.clientId = '271';

    if (useOAuth) {
      this.accessToken = credential;
      this.apiKey = null;
    } else {
      this.apiKey = credential;
      this.accessToken = null;
    }
  }

  get authHeader() {
    if (this.useOAuth) return `Bearer ${this.accessToken}`;
    return `Basic ${btoa(`API_KEY:${this.apiKey}`)}`;
  }

  // ── OAuth2 methods ────────────────────────────────────────────────────────

  initiateOAuth(redirectUri = window.location.origin) {
    if (!this.clientId) {
      console.error('Intervals.icu Client ID is not configured. Check environment variables.');
      alert('Intervals.icu integration is not properly configured. Please contact the administrator.');
      return;
    }

    const state = Math.random().toString(36).substring(2, 15) +
                  Math.random().toString(36).substring(2, 15);
    localStorage.setItem('intervals_oauth_state', state);

    const authUrl = `${this.authURL}?` + new URLSearchParams({
      client_id: this.clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'ACTIVITY:READ,CALENDAR:WRITE,WELLNESS:READ',
      state
    });

    console.log('Redirecting to Intervals.icu OAuth URL:', authUrl);
    window.location.href = authUrl;
  }

  async exchangeCodeForToken(code, state, redirectUri = window.location.origin) {
    try {
      const storedState = localStorage.getItem('intervals_oauth_state');
      if (state !== storedState) {
        throw new Error('Invalid state parameter - possible CSRF attack');
      }

      const response = await fetch(this.tokenProxyURL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ grant_type: 'authorization_code', code, redirect_uri: redirectUri })
      });

      if (!response.ok) {
        const errBody = await response.text();
        throw new Error(`Token exchange failed (${response.status}): ${errBody}`);
      }

      const tokenData = await response.json();
      this.accessToken = tokenData.access_token;
      this.refreshToken = tokenData.refresh_token;
      this.expiresAt = Date.now() + (tokenData.expires_in * 1000);
      this.useOAuth = true;

      localStorage.removeItem('intervals_oauth_state');

      return {
        success: true,
        accessToken: tokenData.access_token,
        refreshToken: tokenData.refresh_token,
        expiresAt: this.expiresAt,
        athleteId: tokenData.athlete_id || tokenData.athlete?.id
      };
    } catch (error) {
      console.error('Error exchanging intervals.icu code for token:', error);
      return { success: false, error: error.message };
    }
  }

  async refreshAccessToken() {
    try {
      if (!this.refreshToken) throw new Error('No refresh token available');

      const response = await fetch(this.tokenProxyURL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ grant_type: 'refresh_token', refresh_token: this.refreshToken })
      });

      if (!response.ok) throw new Error('Failed to refresh token');

      const tokenData = await response.json();
      this.accessToken = tokenData.access_token;
      this.refreshToken = tokenData.refresh_token;
      this.expiresAt = Date.now() + (tokenData.expires_in * 1000);

      return {
        success: true,
        accessToken: tokenData.access_token,
        refreshToken: tokenData.refresh_token,
        expiresAt: this.expiresAt
      };
    } catch (error) {
      console.error('Error refreshing intervals.icu token:', error);
      return { success: false, error: error.message };
    }
  }

  async ensureValidToken() {
    if (!this.accessToken) throw new Error('No access token available');
    if (this.expiresAt && Date.now() > (this.expiresAt - 5 * 60 * 1000)) {
      const result = await this.refreshAccessToken();
      if (!result.success) throw new Error('Failed to refresh token: ' + result.error);
    }
  }

  // ── Data fetching ─────────────────────────────────────────────────────────

  async getRecentWorkouts(days = 14) {
    try {
      if (this.useOAuth) await this.ensureValidToken();

      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - days);

      const response = await fetch(
        `${this.baseURL}/athlete/${this.athleteId}/activities?oldest=${startDate.toISOString().split('T')[0]}&newest=${endDate.toISOString().split('T')[0]}`,
        { headers: { 'Authorization': this.authHeader, 'Content-Type': 'application/json' } }
      );

      if (!response.ok) throw new Error('Failed to fetch workouts');

      const data = await response.json();
      return this.processWorkoutData(data);
    } catch (error) {
      console.error('Error fetching workouts:', error);
      return null;
    }
  }

  async getPlannedWorkouts() {
    try {
      if (this.useOAuth) await this.ensureValidToken();

      const today = new Date();
      const nextWeek = new Date();
      nextWeek.setDate(today.getDate() + 14);

      const response = await fetch(
        `${this.baseURL}/athlete/${this.athleteId}/events?oldest=${today.toISOString().split('T')[0]}&newest=${nextWeek.toISOString().split('T')[0]}`,
        { headers: { 'Authorization': this.authHeader, 'Content-Type': 'application/json' } }
      );

      if (!response.ok) throw new Error('Failed to fetch planned workouts');

      const data = await response.json();
      return this.processPlannedWorkoutData(data);
    } catch (error) {
      console.error('Error fetching planned workouts:', error);
      return null;
    }
  }

  async createPlannedWorkout(workoutData) {
    try {
      if (this.useOAuth) await this.ensureValidToken();

      const eventData = {
        start_date_local: workoutData.date,
        name: workoutData.name,
        description: workoutData.description || '',
        category: workoutData.type || 'Workout',
        planned_duration: workoutData.plannedDuration || null,
        planned_distance: workoutData.plannedDistance || null
      };

      const response = await fetch(`${this.baseURL}/athlete/${this.athleteId}/events`, {
        method: 'POST',
        headers: { 'Authorization': this.authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify(eventData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create workout: ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating workout:', error);
      throw error;
    }
  }

  createWorkout(workoutData) {
    return this.createPlannedWorkout(workoutData);
  }

  async getWellness(days = 14) {
    try {
      if (this.useOAuth) await this.ensureValidToken();

      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - days);

      const response = await fetch(
        `${this.baseURL}/athlete/${this.athleteId}/wellness?oldest=${startDate.toISOString().split('T')[0]}&newest=${endDate.toISOString().split('T')[0]}`,
        { headers: { 'Authorization': this.authHeader, 'Content-Type': 'application/json' } }
      );

      // 403 = scope not granted (OAuth) or inaccessible — degrade silently
      if (!response.ok) return null;

      const data = await response.json();
      if (!Array.isArray(data)) return null;

      return data
        .map(d => ({
          date: d.id,
          hrv: d.hrv ?? null,
          restingHR: d.restingHR ?? null,
          sleepSecs: d.sleepSecs ?? null,
          sleepScore: d.sleepScore ?? null,
          feel: d.feel ?? null,
          motivation: d.motivation ?? null,
          legscore: d.legscore ?? null,
          readiness: d.readiness ?? null,
        }))
        .filter(d => {
          // keep only records that have at least one useful value
          const { date: _d, ...rest } = d; // eslint-disable-line no-unused-vars
          return Object.values(rest).some(v => v !== null);
        })
        .sort((a, b) => b.date.localeCompare(a.date)); // newest first
    } catch {
      return null;
    }
  }

  // ── Data processing ───────────────────────────────────────────────────────

  processWorkoutData(rawData) {
    console.log('Raw workout data from Intervals.icu:', rawData);

    if (!rawData || !Array.isArray(rawData)) {
      console.error('Invalid workout data received:', rawData);
      return [];
    }

    return rawData.map((workout) => ({
        id: workout.id,
        date: workout.start_date || workout.start_date_local,
        type: normalizeActivityType(workout.type || workout.sport_type || workout.category),
        name: workout.name || workout.title,
        duration: workout.moving_time || workout.elapsed_time || workout.duration,
        distance: workout.distance,
        elevation: workout.total_elevation_gain || workout.elevation_gain || null,
        averagePower: workout.avg_watts || workout.average_watts || null,
        maxPower: workout.max_watts || null,
        averageHeartRate: workout.avg_heartrate || workout.average_heartrate || null,
        maxHeartRate: workout.max_heartrate || null,
        tss: workout.icu_training_load || workout.training_stress_score || workout.tss || null,
        feeling: workout.feel || workout.perceived_exertion || null,
        description: workout.description || workout.notes || null,
        source: 'intervals'
      }));
  }

  processPlannedWorkoutData(rawData) {
    return rawData.map(event => ({
      id: event.id,
      date: event.start_date_local,
      type: event.category,
      name: event.name,
      description: event.description,
      planned_duration: event.planned_duration,
      planned_distance: event.planned_distance,
      category: event.category
    }));
  }
}
